import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { twilioService } from '../../../lib/twilioService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SMSBackupSetup = ({ onComplete, onBack }) => {
  const { user, userProfile } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState(userProfile?.phone || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');

  const formatPhoneNumber = (value) => {
    const cleaned = value?.replace(/\D/g, '');
    if (cleaned?.length <= 3) return cleaned;
    if (cleaned?.length <= 6) return `(${cleaned?.slice(0, 3)}) ${cleaned?.slice(3)}`;
    return `(${cleaned?.slice(0, 3)}) ${cleaned?.slice(3, 6)}-${cleaned?.slice(6, 10)}`;
  };

  const handleSendCode = async () => {
    if (!phoneNumber || phoneNumber?.replace(/\D/g, '')?.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    setSending(true);
    setError('');

    // Generate 6-digit code
    const code = Math.floor(100000 + Math.random() * 900000)?.toString();
    setGeneratedCode(code);

    const message = `Your TrainaryGym 2FA verification code is: ${code}. This code expires in 10 minutes.`;
    
    const { error: smsError } = await twilioService?.sendSMS(
      phoneNumber?.replace(/\D/g, ''),
      message,
      '2fa_verification'
    );

    setSending(false);

    if (smsError) {
      setError('Failed to send verification code. Please try again.');
    } else {
      setCodeSent(true);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode?.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setVerifying(true);
    setError('');

    // Verify the code
    setTimeout(() => {
      if (verificationCode === generatedCode) {
        onComplete();
      } else {
        setError('Invalid verification code. Please try again.');
        setVerifying(false);
      }
    }, 1000);
  };

  const handleResendCode = () => {
    setVerificationCode('');
    setCodeSent(false);
    setError('');
    handleSendCode();
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Setup SMS Backup</h2>
          <p className="text-sm text-muted-foreground mt-1">Verify your phone number for backup access</p>
        </div>
        <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors">
          <Icon name="ArrowLeft" size={20} />
        </button>
      </div>
      {/* Phone Number Input */}
      {!codeSent ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Phone Number
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                <Icon name="Phone" size={20} />
              </div>
              <Input
                type="tel"
                placeholder="(555) 123-4567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(formatPhoneNumber(e?.target?.value))}
                className="pl-10"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Enter your mobile number to receive verification codes
            </p>
          </div>

          {/* International Support Notice */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Globe" size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">International Numbers</p>
                <p>We support international phone numbers. Standard SMS rates may apply.</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onBack}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              onClick={handleSendCode}
              disabled={sending || !phoneNumber}
              className="flex-1"
            >
              {sending ? 'Sending...' : 'Send Verification Code'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Code Sent Confirmation */}
          <div className="bg-success/10 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-foreground mb-1">Code Sent!</p>
                <p className="text-muted-foreground">
                  We've sent a 6-digit verification code to {phoneNumber}
                </p>
              </div>
            </div>
          </div>

          {/* Verification Code Input */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Enter Verification Code
            </label>
            <Input
              type="text"
              placeholder="000000"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
              maxLength={6}
              className="text-center text-2xl tracking-widest font-mono"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Enter the 6-digit code sent to your phone
            </p>
          </div>

          {/* Resend Code */}
          <div className="text-center">
            <button
              onClick={handleResendCode}
              className="text-sm text-primary hover:underline"
            >
              Didn't receive the code? Resend
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setCodeSent(false)}
              className="flex-1"
            >
              Change Number
            </Button>
            <Button
              onClick={handleVerify}
              disabled={verifying || verificationCode?.length !== 6}
              className="flex-1"
            >
              {verifying ? 'Verifying...' : 'Verify & Continue'}
            </Button>
          </div>
        </div>
      )}
      {/* Security Notice */}
      <div className="mt-6 p-4 rounded-lg bg-warning/5 border border-warning/20">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Security Notice</p>
            <p>
              SMS backup is less secure than authenticator apps. We recommend using it only as a backup method.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSBackupSetup;